import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';

const ReferralTeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ totalBonusPaid: 0, totalBonusOrders: 0, totalTeams: 0 });
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [earningsReport, setEarningsReport] = useState(null);
  const [settings, setSettings] = useState({ enabled: true, bonusPerOrderPaisa: 200, maxTeamSize: 10 });
  const [showSettings, setShowSettings] = useState(false);

  const loadTeams = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getReferralOverview({ search, page, limit: 20 });
      if (data.success) {
        setTeams(data.data.teams);
        setTotalPages(data.data.totalPages);
        setStats({
          totalBonusPaid: data.data.totalBonusPaid,
          totalBonusOrders: data.data.totalBonusOrders,
          totalTeams: data.data.totalTeams,
        });
      }
    } catch (err) {
      console.error('Failed to load referral teams:', err);
    }
    setLoading(false);
  }, [search, page]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await adminAPI.getReferralTeamSettings();
      if (data.success) {
        setSettings(data.data.referralTeamSystem);
      }
    } catch (err) {
      console.error('Failed to load referral settings:', err);
    }
  }, []);

  const saveSettings = async () => {
    try {
      const { data } = await adminAPI.updateReferralTeamSettings(settings);
      if (data.success) {
        alert('Settings saved successfully');
        setShowSettings(false);
      }
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.message || err.message));
    }
  };

  useEffect(() => {
    loadTeams();
    loadSettings();
  }, [loadTeams, loadSettings]);

  const loadPartnerDetails = async (partnerId) => {
    setDetailsLoading(true);
    setSelectedPartner(partnerId);
    try {
      const { data } = await adminAPI.getPartnerReferralDetails(partnerId);
      if (data.success) {
        setPartnerDetails(data.data);
      }
    } catch (err) {
      console.error('Failed to load partner details:', err);
    }
    setDetailsLoading(false);
  };

  const loadEarningsReport = async () => {
    try {
      const { data } = await adminAPI.getReferralEarningsReport({});
      if (data.success) {
        setEarningsReport(data.data);
      }
    } catch (err) {
      console.error('Failed to load earnings report:', err);
    }
  };

  const handleOverride = async (referrerId, memberId, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this team member?`)) return;
    try {
      const { data } = await adminAPI.overrideTeamMember(referrerId, memberId, action);
      if (data.success) {
        alert(`Team member ${action}ed successfully`);
        if (selectedPartner) loadPartnerDetails(selectedPartner);
        loadTeams();
      }
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const statusBadge = (status) => {
    const colors = { pending: '#f59e0b', accepted: '#10b981', rejected: '#ef4444' };
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: colors[status] || '#6b7280'
      }}>
        {status?.toUpperCase()}
      </span>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
        Referral Team Management
      </h1>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>Total Teams</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937' }}>{stats.totalTeams}</div>
        </div>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>Total Bonus Paid</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>Rs {stats.totalBonusPaid}</div>
        </div>
        <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>Total Bonus Orders</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#3b82f6' }}>{stats.totalBonusOrders}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Search by name, phone, or referral code..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{
            flex: 1, padding: '10px 16px', borderRadius: '8px',
            border: '1px solid #d1d5db', fontSize: '14px'
          }}
        />
        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db',
            background: 'white', cursor: 'pointer', fontWeight: '600'
          }}
        >
          Settings
        </button>
        <button
          onClick={loadEarningsReport}
          style={{
            padding: '10px 20px', borderRadius: '8px', border: 'none',
            background: '#3b82f6', color: 'white', cursor: 'pointer', fontWeight: '600'
          }}
        >
          Earnings Report
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div style={{
          background: '#fff', padding: '20px', borderRadius: '12px',
          border: '1px solid #d1d5db', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontWeight: 'bold', marginBottom: '16px' }}>Referral Team Settings</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>System Enabled</label>
              <select
                value={settings.enabled ? 'true' : 'false'}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.value === 'true' })}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Bonus Per Order (Paisa)</label>
              <input
                type="number"
                value={settings.bonusPerOrderPaisa}
                onChange={(e) => setSettings({ ...settings, bonusPerOrderPaisa: parseInt(e.target.value) || 0 })}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
              <small style={{ color: '#9ca3af' }}>200 paisa = Rs 2</small>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Max Team Size</label>
              <input
                type="number"
                value={settings.maxTeamSize}
                onChange={(e) => setSettings({ ...settings, maxTeamSize: parseInt(e.target.value) || 10 })}
                min="1"
                max="50"
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>
          </div>
          <button
            onClick={saveSettings}
            style={{
              padding: '8px 20px', borderRadius: '6px', border: 'none',
              background: '#10b981', color: 'white', cursor: 'pointer', fontWeight: '600'
            }}
          >
            Save Settings
          </button>
        </div>
      )}

      {/* Earnings Report Modal */}
      {earningsReport && (
        <div style={{
          background: '#f0fdf4', padding: '16px', borderRadius: '12px',
          border: '1px solid #86efac', marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontWeight: 'bold' }}>Earnings Report</h3>
            <button onClick={() => setEarningsReport(null)} style={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: '18px' }}>x</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <div><strong>Total Paid:</strong> Rs {earningsReport.totalBonusPaid}</div>
            <div><strong>Total Orders:</strong> {earningsReport.totalOrders}</div>
            <div><strong>Referrers:</strong> {earningsReport.uniqueReferrers}</div>
            <div><strong>Members:</strong> {earningsReport.uniqueMembers}</div>
          </div>
          {earningsReport.topReferrers?.length > 0 && (
            <>
              <h4 style={{ fontWeight: '600', marginBottom: '8px' }}>Top Referrers</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #d1d5db' }}>
                    <th style={{ textAlign: 'left', padding: '6px' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '6px' }}>Phone</th>
                    <th style={{ textAlign: 'right', padding: '6px' }}>Earnings</th>
                    <th style={{ textAlign: 'right', padding: '6px' }}>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {earningsReport.topReferrers.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '6px' }}>{r.name}</td>
                      <td style={{ padding: '6px' }}>{r.phone}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>Rs {r.totalEarnings}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>{r.totalOrders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Teams Table */}
      <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
        ) : teams.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No teams found</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>Partner</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>Referral Code</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>Team Size</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>Accepted</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>Pending</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>Earnings</th>
                <th style={{ textAlign: 'center', padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.partnerId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: '600' }}>{team.name}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{team.phone}</div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <code style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>
                      {team.referralCode}
                    </code>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>{team.teamSize}/10</td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#10b981', fontWeight: '600' }}>{team.acceptedMembers}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', color: '#f59e0b', fontWeight: '600' }}>{team.pendingMembers}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600' }}>Rs {team.totalReferralEarnings}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <button
                      onClick={() => loadPartnerDetails(team.partnerId)}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
                        background: 'white', cursor: 'pointer', fontSize: '13px'
                      }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '16px' }}>
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: page <= 1 ? 'default' : 'pointer' }}
            >
              Previous
            </button>
            <span style={{ padding: '6px 12px' }}>Page {page} of {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: page >= totalPages ? 'default' : 'pointer' }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Partner Detail Modal */}
      {selectedPartner && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000
        }} onClick={() => { setSelectedPartner(null); setPartnerDetails(null); }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '24px',
            maxWidth: '700px', width: '90%', maxHeight: '80vh', overflowY: 'auto'
          }} onClick={e => e.stopPropagation()}>
            {detailsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
            ) : partnerDetails ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>{partnerDetails.partner.name}</h2>
                    <div style={{ color: '#6b7280' }}>{partnerDetails.partner.phone}</div>
                    <code style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' }}>
                      {partnerDetails.partner.referralCode}
                    </code>
                  </div>
                  <button
                    onClick={() => { setSelectedPartner(null); setPartnerDetails(null); }}
                    style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer' }}
                  >
                    x
                  </button>
                </div>

                {partnerDetails.partner.referredBy && (
                  <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                    <strong>Referred by:</strong> {partnerDetails.partner.referredBy.name} ({partnerDetails.partner.referredBy.phone})
                    — Status: {statusBadge(partnerDetails.partner.referredByStatus)}
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    Total Referral Earnings: Rs {partnerDetails.team.totalReferralEarnings}
                  </div>
                </div>

                <h3 style={{ fontWeight: '600', marginBottom: '12px' }}>Team Members</h3>
                {partnerDetails.team.members.length === 0 ? (
                  <div style={{ color: '#6b7280', padding: '20px', textAlign: 'center' }}>No team members</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #d1d5db' }}>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Phone</th>
                        <th style={{ textAlign: 'center', padding: '8px' }}>Status</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Orders</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Earnings</th>
                        <th style={{ textAlign: 'center', padding: '8px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partnerDetails.team.members.map((m) => (
                        <tr key={m.partnerId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px' }}>{m.name}</td>
                          <td style={{ padding: '8px' }}>{m.phone}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{statusBadge(m.status)}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{m.totalOrdersDelivered}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>Rs {m.totalReferralEarnings}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {m.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleOverride(selectedPartner, m.partnerId, 'accept')}
                                  style={{ padding: '4px 8px', borderRadius: '4px', border: 'none', background: '#10b981', color: 'white', cursor: 'pointer', marginRight: '4px', fontSize: '12px' }}
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => handleOverride(selectedPartner, m.partnerId, 'reject')}
                                  style={{ padding: '4px 8px', borderRadius: '4px', border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer', fontSize: '12px' }}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleOverride(selectedPartner, m.partnerId, 'remove')}
                              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', marginLeft: '4px', fontSize: '12px' }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {partnerDetails.recentEarnings?.length > 0 && (
                  <>
                    <h3 style={{ fontWeight: '600', marginTop: '20px', marginBottom: '12px' }}>Recent Referral Earnings</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #d1d5db' }}>
                          <th style={{ textAlign: 'left', padding: '6px' }}>Order</th>
                          <th style={{ textAlign: 'left', padding: '6px' }}>Member</th>
                          <th style={{ textAlign: 'right', padding: '6px' }}>Amount</th>
                          <th style={{ textAlign: 'right', padding: '6px' }}>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerDetails.recentEarnings.map((e, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '6px' }}>{e.orderNumber}</td>
                            <td style={{ padding: '6px' }}>{e.memberName}</td>
                            <td style={{ padding: '6px', textAlign: 'right' }}>Rs {e.amount}</td>
                            <td style={{ padding: '6px', textAlign: 'right', fontSize: '12px' }}>{new Date(e.createdAt).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralTeamManagement;
